import {
    createContext,
    useEffect,
    useState,
    FC,
    ReactNode,
    useMemo,
    useCallback,
} from 'react';
import Keycloak from 'keycloak-js';
import isElectron from 'is-electron';
import { getElectronAPI } from 'flow/utils/electron-api';
import { parseJwt } from './authservice.utils';
import {
    exchangeCodeForTokens,
    electronAppAuthLogout,
} from './authservice.service';
import {
    AuthServiceContextProps,
    KeycloakConfigType,
    TokenType,
} from './authtypes';
import {
    getAuthServiceConfig,
    isAuthServiceConfigured,
    isProduction,
} from './authservice.config';
import { signOut } from 'flow/actions/app-actions';
import homeClient from 'flow/api-clients/home-client';
import { useAppDispatch } from 'flow/hooks/useRedux';

const AuthServiceContext = createContext<AuthServiceContextProps | null>(null);

interface AuthServiceProviderProps {
    children: ReactNode;
}

const AuthServiceProvider: FC<AuthServiceProviderProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const [keycloakInstance, setKeycloakInstance] = useState<Keycloak | null>(
        null,
    );
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
    const [isAuthInitError, setIsAuthInitError] = useState<boolean>(false);

    const { authUrl, authRealm, authClientId } = getAuthServiceConfig() || {};

    const keycloakConfigs: KeycloakConfigType = {
        url: authUrl,
        realm: authRealm,
        clientId: authClientId,
    };

    const initializeKeycloak = async () => {
        const keycloak = new Keycloak(keycloakConfigs);
        const isCustomProtocol = isElectron() && isProduction();
        const auth = await keycloak.init({
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri: `${isCustomProtocol ? 'flowdesktop://app' : window.location.origin}/silent-check-sso.html`,
        });
        return { keycloak, auth };
    };

    const handleElectronAppAuthLogin = async (
        keycloak: Keycloak,
        tokens: TokenType,
    ) => {
        if (!tokens?.authorizationCode || !keycloak) return;

        try {
            const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
            if (!codeVerifier) {
                console.error('No code verifier found in sessionStorage');
                setAuthenticated(false);
                return;
            }

            const tokenData = await exchangeCodeForTokens(
                tokens.authorizationCode,
                codeVerifier,
                keycloakConfigs,
            );

            if (!tokenData) {
                setAuthenticated(false);
                return;
            }

            keycloak.token = tokenData.access_token;
            keycloak.refreshToken = tokenData.refresh_token;

            if (tokenData.id_token) {
                keycloak.idToken = tokenData.id_token;
            }

            keycloak.tokenParsed = tokenData.access_token
                ? parseJwt(tokenData.access_token)
                : null;
            keycloak.refreshTokenParsed = tokenData.refresh_token
                ? parseJwt(tokenData.refresh_token)
                : null;
            keycloak.idTokenParsed = tokenData.id_token
                ? parseJwt(tokenData.id_token)
                : null;
            keycloak.authenticated = true;
            sessionStorage.removeItem('pkce_code_verifier');

            setKeycloakInstance(keycloak);
            setAuthenticated(true);
        } catch (error) {
            console.error('Failed to exchange code for tokens:', error);
            setAuthenticated(false);
        }
    };

    const disableAuthService = () => {
        setIsAuthLoading(false);
        setKeycloakInstance(null);
        setAuthenticated(false);
    };

    useEffect(() => {
        setIsAuthLoading(true);
        let isMounted = true;

        const initAuth = async () => {
            if (!isAuthServiceConfigured()) {
                disableAuthService();
                return;
            }

            try {
                const { keycloak, auth } = await initializeKeycloak();

                setKeycloakInstance(keycloak);
                setIsAuthLoading(false);
                setIsAuthInitError(false);

                if (isElectron()) {
                    const electronAPI = getElectronAPI();
                    if (electronAPI) {
                        electronAPI.onAuthServiceLoginSuccess(
                            async (tokens: TokenType) => {
                                if (isMounted) {
                                    await handleElectronAppAuthLogin(
                                        keycloak,
                                        tokens,
                                    );
                                }
                            },
                        );
                    } else {
                        setAuthenticated(false);
                    }
                } else if (isMounted) {
                    setAuthenticated(auth);
                }
            } catch (_) {
                disableAuthService();
                setIsAuthInitError(true);
            }
        };

        initAuth();
        checkAuthServiceError();

        return () => {
            isMounted = false;
            if (isElectron()) {
                const electronAPI = getElectronAPI();
                if (electronAPI) {
                    electronAPI.removeAuthServiceLoginListener();
                }
            }
        };
    }, []);

    const checkAuthServiceError = () => {
        const hash = window.location.hash;
        const searchParams = new URLSearchParams(window.location.search);

        if (
            hash.includes('error=login_required') ||
            searchParams.get('error') === 'login_required'
        ) {
            dispatch(signOut());
        }
    };

    const handleExpiredToken = useCallback(async () => {
        if (keycloakInstance) {
            try {
                const refreshed = await keycloakInstance.updateToken(30);
                if (refreshed) {
                    console.log('AuthService: AuthService token refreshed');
                } else {
                    console.warn(
                        'AuthService: AuthService token not refreshed, still valid',
                    );
                }
            } catch {
                console.error(
                    'AuthService: Failed to refresh AuthService token',
                );
            }
        }
    }, [keycloakInstance]);

    useEffect(() => {
        if (keycloakInstance && authenticated) {
            keycloakInstance.onTokenExpired = handleExpiredToken;
        }
    }, [keycloakInstance, authenticated, handleExpiredToken]);

    const logout = useCallback(async () => {
        if (isElectron()) {
            if (keycloakInstance) {
                await electronAppAuthLogout(keycloakInstance);
                keycloakInstance.clearToken();
            }
        } else {
            keycloakInstance?.logout();
        }
        homeClient.removeAuthServiceToken();
        setAuthenticated(false);
    }, [keycloakInstance]);

    const authServiceParameters: AuthServiceContextProps = useMemo(
        () => ({
            keycloak: keycloakInstance,
            authenticated,
            isAuthLoading,
            logout,
            isAuthServiceEnabled: isAuthServiceConfigured() && !isAuthInitError,
        }),
        [
            keycloakInstance,
            authenticated,
            isAuthLoading,
            isAuthInitError,
            logout,
        ],
    );

    return (
        <AuthServiceContext.Provider value={authServiceParameters}>
            {children}
        </AuthServiceContext.Provider>
    );
};

export { AuthServiceProvider, AuthServiceContext };
